/**
 * WhatsApp-style chat bubble preview — used by the campaign composer to show a
 * single, unified preview of the outgoing message (header media + body text +
 * footer + CTA buttons + timestamp), mirroring how the message renders on a
 * recipient's phone.
 */
import React, { useState } from 'react';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';
import { OpenInNew, Phone, ReplyAll, Description, FormatListBulleted } from '@mui/icons-material';
import MediaPreviewDialog from '@/components/common/MediaPreviewDialog';

/** One card of a CAROUSEL template — media header + body + up to 2 buttons,
 *  the same shape as a regular preview but scoped to a single card. */
export interface WhatsAppPreviewCarouselCard {
  mediaUrl?: string | null;
  mediaType?: 'IMAGE' | 'VIDEO' | undefined;
  mediaFileName?: string | undefined;
  mediaLoading?: boolean | undefined;
  bodyText: string;
  buttons?: Array<{ type: string; text: string }> | undefined;
}

/** A List Message's drawer contents — additive to the props below, so every
 *  existing caller (campaigns, templates, the workflow canvas) is unaffected. */
export interface WhatsAppPreviewListDrawer {
  /** Label of the button that opens the drawer (e.g. "Menu"). */
  buttonText: string;
  sections: Array<{
    title?: string | undefined;
    rows: Array<{ title: string; description?: string | undefined }>;
  }>;
}

interface WhatsAppPreviewBubbleProps {
  mediaUrl?: string | null;
  mediaType?: 'IMAGE' | 'VIDEO' | 'DOCUMENT' | undefined;
  mediaFileName?: string | undefined;
  mediaLoading?: boolean | undefined;
  /** Plain TEXT header — a List/Button message's alternative to a media
   *  header. Ignored when `mediaType`/`mediaUrl` are also given. */
  headerText?: string | undefined;
  bodyText: string;
  footerText?: string | undefined;
  buttons?: Array<{ type: string; text: string }> | undefined;
  emptyHint?: string;
  /** CAROUSEL templates: mutually exclusive with the top-level media/footer/buttons
   *  above (Meta only allows a plain body as the carousel's intro text) — when
   *  non-empty, a horizontally-scrollable card strip renders below the body instead. */
  carousel?: WhatsAppPreviewCarouselCard[] | undefined;
  /** A List Message's opener button + its sections/rows, rendered as an
   *  always-expanded preview (the real drawer opens only on tap) — mutually
   *  exclusive with `carousel`/`buttons`. */
  list?: WhatsAppPreviewListDrawer | undefined;
}

const CarouselCardPreview: React.FC<{ card: WhatsAppPreviewCarouselCard }> = ({ card }) => (
  <Paper
    elevation={0}
    sx={{ minWidth: 190, maxWidth: 190, flexShrink: 0, borderRadius: 2, overflow: 'hidden', border: '1px solid #e9edef' }}
  >
    {card.mediaLoading ? (
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ height: 110, background: '#f0f2f5' }}>
        <CircularProgress size={16} />
      </Box>
    ) : card.mediaType && card.mediaUrl ? (
      card.mediaType === 'IMAGE' ? (
        <Box component="img" src={card.mediaUrl} alt="card header" sx={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }} />
      ) : (
        <Box component="video" src={card.mediaUrl} controls sx={{ width: '100%', height: 110, display: 'block' }} />
      )
    ) : (
      <Box sx={{ height: 110, background: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" color="text.disabled">
          {card.mediaFileName || (card.mediaType ? card.mediaType.toLowerCase() : 'media')}
        </Typography>
      </Box>
    )}
    <Box sx={{ p: 1 }}>
      <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, color: '#111b21', display: 'block' }}>
        {card.bodyText || 'Card text…'}
      </Typography>
    </Box>
    {card.buttons && card.buttons.length > 0 && (
      <Box>
        {card.buttons.map((b, i) => (
          <Box key={i} sx={{ borderTop: '1px solid #e9edef', textAlign: 'center', py: 0.75 }}>
            <Typography variant="caption" sx={{ color: '#00a5f4', display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}>
              {b.type === 'PHONE_NUMBER' ? <Phone sx={{ fontSize: 13 }} /> : b.type === 'URL' ? <OpenInNew sx={{ fontSize: 13 }} /> : <ReplyAll sx={{ fontSize: 13 }} />}
              {b.text}
            </Typography>
          </Box>
        ))}
      </Box>
    )}
  </Paper>
);

const WhatsAppPreviewBubble: React.FC<WhatsAppPreviewBubbleProps> = ({
  mediaUrl, mediaType, mediaFileName, mediaLoading, headerText, bodyText, footerText, buttons, emptyHint, carousel, list,
}) => {
  const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  const isCarousel = !!carousel && carousel.length > 0;
  /** Document headers open in-app; the preview must never send the composer
   *  off to the signed URL. */
  const [docPreviewOpen, setDocPreviewOpen] = useState(false);

  return (
    <Box sx={{ background: '#e5ddd5', borderRadius: 2, p: 2 }}>
      <Paper elevation={1} sx={{ maxWidth: 320, borderRadius: 2, overflow: 'hidden', mx: 'auto' }}>
        {!isCarousel && (mediaLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center" sx={{ height: 140, background: '#f0f2f5' }}>
            <CircularProgress size={20} />
          </Box>
        ) : mediaType && mediaUrl ? (
          mediaType === 'IMAGE' ? (
            <Box component="img" src={mediaUrl} alt="header" sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', display: 'block' }} />
          ) : mediaType === 'VIDEO' ? (
            <Box component="video" src={mediaUrl} controls sx={{ width: '100%', maxHeight: 200, display: 'block' }} />
          ) : (
            <Box sx={{ p: 1.5, background: '#f0f2f5', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Description fontSize="small" color="action" />
              <Typography
                variant="body2"
                component="button"
                type="button"
                onClick={() => setDocPreviewOpen(true)}
                aria-label={`Preview ${mediaFileName || 'Document'}`}
                sx={{
                  p: 0,
                  border: 'none',
                  bgcolor: 'transparent',
                  color: 'primary.main',
                  cursor: 'pointer',
                  font: 'inherit',
                  textAlign: 'left',
                  textDecoration: 'underline',
                }}
              >
                {mediaFileName || 'Document'}
              </Typography>
            </Box>
          )
        ) : !isCarousel && headerText ? (
          <Box sx={{ p: 1.5, pb: 0 }}>
            <Typography variant="body2" fontWeight={700} sx={{ color: '#111b21' }}>
              {headerText}
            </Typography>
          </Box>
        ) : null)}

        <Box sx={{ p: 1.5 }}>
          {bodyText ? (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: '#111b21' }}>
              {bodyText}
            </Typography>
          ) : !isCarousel ? (
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              {emptyHint || 'Your message preview will appear here…'}
            </Typography>
          ) : null}
          {!isCarousel && footerText && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              {footerText}
            </Typography>
          )}
          {!isCarousel && (
            <Typography variant="caption" color="text.disabled" display="block" textAlign="right" mt={0.5}>
              {timestamp}
            </Typography>
          )}
        </Box>

        {isCarousel && (
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', px: 1.5, pb: 1.5 }}>
            {carousel!.map((card, i) => (
              <CarouselCardPreview key={i} card={card} />
            ))}
          </Box>
        )}
        {isCarousel && (
          <Typography variant="caption" color="text.disabled" display="block" textAlign="right" px={1.5} pb={1}>
            {timestamp}
          </Typography>
        )}

        {!isCarousel && buttons && buttons.length > 0 && (
          <Box>
            {buttons.map((b, i) => (
              <Box key={i} sx={{ borderTop: '1px solid #e9edef', textAlign: 'center', py: 1 }}>
                <Typography variant="body2" sx={{ color: '#00a5f4', display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}>
                  {b.type === 'PHONE_NUMBER' ? <Phone fontSize="small" /> : b.type === 'URL' ? <OpenInNew fontSize="small" /> : <ReplyAll fontSize="small" />}
                  {b.text}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {!isCarousel && list && (
          <>
            <Box sx={{ borderTop: '1px solid #e9edef', textAlign: 'center', py: 1 }}>
              <Typography variant="body2" sx={{ color: '#00a5f4', display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}>
                <FormatListBulleted fontSize="small" />
                {list.buttonText || 'Menu'}
              </Typography>
            </Box>
            {/* The real drawer only opens on tap — this always-expanded preview
                is what an author needs while building, not a faithful runtime
                replica of the tap interaction. */}
            <Box sx={{ borderTop: '1px solid #e9edef', bgcolor: '#fafafa', maxHeight: 240, overflowY: 'auto' }}>
              {list.sections.map((section, si) => (
                <Box key={si}>
                  {section.title && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', px: 1.5, pt: 1, color: 'text.secondary', fontWeight: 600 }}
                    >
                      {section.title}
                    </Typography>
                  )}
                  {section.rows.map((row, ri) => (
                    <Box key={ri} sx={{ px: 1.5, py: 1, borderBottom: '1px solid #eee' }}>
                      <Typography variant="body2" fontWeight={500} sx={{ color: '#111b21' }}>
                        {row.title || 'Option'}
                      </Typography>
                      {row.description && (
                        <Typography variant="caption" color="text.secondary">
                          {row.description}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </>
        )}
      </Paper>

      {mediaType === 'DOCUMENT' && mediaUrl && (
        <MediaPreviewDialog
          open={docPreviewOpen}
          onClose={() => setDocPreviewOpen(false)}
          url={mediaUrl}
          {...(mediaFileName ? { fileName: mediaFileName } : {})}
        />
      )}
    </Box>
  );
};

export default WhatsAppPreviewBubble;
